import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import Button from 'react-bootstrap/Button';
import { Slideout } from '../slideout';
import { FilterRow } from './FilterRow';
import { ExtractedTermsFilterInput, FieldMetadataMap } from '../../types/extractedTerms';
import { GET_EXTRACTED_TERMS_FIELD_METADATA } from '../../api/queries/extractedTerms';
import LoadingSpinner from '../loading-spinner';
import { ReactComponent as PlusIcon } from '../../shared/icons/plus-icon.svg';
import { ReactComponent as FilterIcon } from '../../shared/icons/filter-icon.svg';
import styles from './FilterSlideout.module.scss';

interface FilterSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  documentType: string;
  initialFilters?: ExtractedTermsFilterInput[];
  onApplyFilters: (filters: ExtractedTermsFilterInput[]) => void;
}

export const FilterSlideout: React.FC<FilterSlideoutProps> = ({
  isOpen,
  onClose,
  documentType,
  initialFilters = [],
  onApplyFilters,
}) => {
  const [filters, setFilters] = useState<ExtractedTermsFilterInput[]>(
    initialFilters.length > 0 ? initialFilters : [{ field: '', operator: 'equals', value: '' }]
  );

  // Fetch field metadata for the document type
  const { data: metadataResponse, loading: metadataLoading } = useQuery(
    GET_EXTRACTED_TERMS_FIELD_METADATA,
    {
      variables: { documentType },
      skip: !documentType || !isOpen,
    }
  );

  const fieldMetadata: FieldMetadataMap = metadataResponse?.extractedTermsFieldMetadata || {};

  // Initialize filters from props when slideout opens
  useEffect(() => {
    if (isOpen) {
      if (initialFilters.length > 0) {
        setFilters(initialFilters);
      } else {
        setFilters([{ field: '', operator: 'equals', value: '' }]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, documentType]);

  const handleAddFilter = () => {
    setFilters([...filters, { field: '', operator: 'equals', value: '' }]);
  };

  const handleRemoveFilter = (index: number) => {
    const newFilters = filters.filter((_, i) => i !== index);
    // Always keep at least one filter row
    if (newFilters.length === 0) {
      setFilters([{ field: '', operator: 'equals', value: '' }]);
    } else {
      setFilters(newFilters);
    }
  };

  const handleFilterChange = (index: number, newFilter: ExtractedTermsFilterInput) => {
    const newFilters = [...filters];
    newFilters[index] = newFilter;
    setFilters(newFilters);
  };

  const handleApply = () => {
    // Filter out incomplete filters (no field selected or no value)
    const validFilters = filters.filter((f) => {
      if (!f.field) return false;
      if (f.value === '' || f.value === null || f.value === undefined) return false;
      if (Array.isArray(f.value) && f.value.length === 0) return false;
      if (Array.isArray(f.value) && f.operator === 'between') {
        // Both min and max must be provided for 'between'
        return f.value[0] !== '' && f.value[1] !== '';
      }
      return true;
    });

    onApplyFilters(validFilters);
    onClose();
  };

  const handleClearAll = () => {
    setFilters([{ field: '', operator: 'equals', value: '' }]);
    onApplyFilters([]);
  };

  const activeFilterCount = filters.filter((f) => f.field && f.value).length;

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className={styles.slideoutTitle}>
          <FilterIcon className={styles.titleIcon} />
          <span>Filter Document Data</span>
          {activeFilterCount > 0 && (
            <span className={styles.filterCount}>({activeFilterCount})</span>
          )}
        </div>
      }
    >
      <div className={styles.slideoutContent}>
        {metadataLoading ? (
          <div className={styles.loadingContainer}>
            <LoadingSpinner />
            <p>Loading filter options...</p>
          </div>
        ) : (
          <>
            <div className={styles.filtersList}>
              {filters.map((filter, index) => (
                <React.Fragment key={index}>
                  <FilterRow
                    filter={filter}
                    fieldMetadata={fieldMetadata}
                    onChange={(newFilter) => handleFilterChange(index, newFilter)}
                    onRemove={() => handleRemoveFilter(index)}
                    canRemove={filters.length > 1}
                  />
                  {/* Show "AND" separator between filters (not after the last one) */}
                  {index < filters.length - 1 && (
                    <div className={styles.andSeparator}>
                      <span>AND</span>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className={styles.addFilterButton}>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleAddFilter}
              >
                <PlusIcon className={styles.plusIcon} />
                Add Filter
              </Button>
            </div>

            <div className={styles.slideoutActions}>
              <Button
                variant="outline-secondary"
                onClick={handleClearAll}
              >
                Clear All
              </Button>
              <div className={styles.rightActions}>
                <Button variant="outline-secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleApply}>
                  Apply Filters
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Slideout>
  );
};
